import { makeObservable, observable, computed, action } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { IdmissionSDKType } from './IdMissionPopup/IdMissionPopup';
import { WebsocketV1 } from 'src/domains/layouts/state/websocketV1/WebsocketV1';
import { ApiResponseWrapper } from 'src_common/common/mobx-utils/ApiResponseWrapper';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { IdentityAPI } from './IdentityAPI';
import { ApiVerifyProgressResponse200Type } from 'src/api_openapi/v2/idMission/apiVerifyProgress';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';

const createIdentity = (
    identityAPI: IdentityAPI,
    websocketV1: WebsocketV1,
    common: Common
): AutoMap<[number], ApiResponseWrapper<ApiVerifyProgressResponse200Type | null>> =>
    new AutoMap(
        ([userId]) =>
            new ApiResponseWrapper(
                'getVerifyProgress',
                null,
                async (): Promise<ApiVerifyProgressResponse200Type | null> => {
                    await new Promise((resolve) => setTimeout(resolve, 300));

                    const response = await identityAPI.getVerifyProgress(userId);

                    if (response.status === 200) {
                        return response.body;
                    }

                    return null;
                },
                (refresh) => {
                    const isAuthorizedUser = common.session.userId === userId;
                    if (isAuthorizedUser) {
                        refresh();
                    }

                    websocketV1.subscribe(`${userId}:Identity`, refresh);

                    return (): void => {
                        websocketV1.unsubscribe(`${userId}:Identity`, refresh);
                    };
                }
            )
    );

export class IdentityState {
    private static readonly DEFAULT_VERIFICATION_TYPE: IdmissionSDKType = 'IdCardAndFaceProofOfAddress';
    private identityVerifyProgress: AutoMap<[number], ApiResponseWrapper<ApiVerifyProgressResponse200Type | null>>;

    @observable public unauthorizedUserId: number | null = null;
    private identityAPI: IdentityAPI;
    private localStorageState: LocalStorageState;
    private basicData: BasicDataModel;

    public constructor(private readonly common: Common) {
        makeObservable(this);
        this.identityAPI = IdentityAPI.get(this.common);
        this.identityVerifyProgress = createIdentity(this.identityAPI, WebsocketV1.get(this.common), this.common);
        this.localStorageState = LocalStorageState.get(common);
        this.basicData = BasicDataModel.get(common);
    }

    public static get = AutoWeakMap.create((common: Common) => new IdentityState(common));

    @computed public get processIdMission(): { type: IdmissionSDKType | null; isOpen: boolean; id: number | null } {
        const defResponse = { type: null, isOpen: false, id: null };
        const userId = this.common.session.userId ?? this.unauthorizedUserId;
        if (userId === null) {
            return defResponse;
        }
        const verifyProcess = this.identityVerifyProgress.get([userId]).data;

        if (verifyProcess?.progress === 'requested') {
            if (verifyProcess.triggerOrigin === 'manual') {
                const showSecondThreshold = this.localStorageState.kycSecondThresholdShowPopup.getValue();
                return {
                    type: verifyProcess.verificationVariant,
                    isOpen: showSecondThreshold,
                    id: verifyProcess.id,
                };
            }

            if (this.hasDepositRestriction) {
                const showSecondThreshold = this.localStorageState.kycSecondThresholdShowPopup.getValue();
                return {
                    type: verifyProcess.verificationVariant,
                    isOpen: showSecondThreshold,
                    id: verifyProcess.id,
                };
            } else {
                const showFirstThreshold = this.localStorageState.kycFirstThresholdShowPopup.getValue();
                return {
                    type: verifyProcess.verificationVariant,
                    isOpen: showFirstThreshold,
                    id: verifyProcess.id,
                };
            }
        }

        return defResponse;
    }

    private getBlockingNotificationType(): string | null {
        const customerData = this.basicData.basicDataReady;
        if (customerData === null) {
            return null;
        }

        const allNotifications = customerData.prompts?.notificationList ?? [];
        const activeNotifications = allNotifications.filter((notification) => notification.active);

        const kycNotification = activeNotifications.find((n) => n.name === 'kycDepositRestriction');
        if (kycNotification?.active === true) {
            return 'KYC_DEPOSIT_RESTRICTION';
        }

        return null;
    }

    @computed public get hasDepositRestriction(): boolean {
        return this.getBlockingNotificationType() === 'KYC_DEPOSIT_RESTRICTION';
    }

    @computed public get isManualTrigger(): boolean {
        const userId = this.common.session.userId ?? this.unauthorizedUserId;
        if (userId === null) {
            return false;
        }
        const verifyProcess = this.identityVerifyProgress.get([userId]).data;
        return verifyProcess?.triggerOrigin === 'manual';
    }

    @computed public get shouldShowSecondThresholdVariant(): boolean {
        return this.hasDepositRestriction || this.isManualTrigger;
    }

    @action public runManualTrigger = async (userId: number | null): Promise<void> => {
        if (userId === null) {
            return;
        }
        await this.identityAPI.runManualTrigger(userId, IdentityState.DEFAULT_VERIFICATION_TYPE);

        this.localStorageState.kycFirstThresholdShowPopup.setValue(true);
    };

    @action public setUnauthorizedUserId = (userId: number): void => {
        this.unauthorizedUserId = userId;
    };

    @action public closeIdMissionPopup(): void {
        if (this.shouldShowSecondThresholdVariant) {
            this.localStorageState.kycSecondThresholdShowPopup.setValue(false);
        } else {
            this.localStorageState.kycFirstThresholdShowPopup.setValue(false);
        }
    }

    @action public openIdMissionPopup(): void {
        if (this.shouldShowSecondThresholdVariant) {
            this.localStorageState.kycSecondThresholdShowPopup.setValue(true);
        } else {
            this.localStorageState.kycFirstThresholdShowPopup.setValue(true);
        }
    }

    @action public refreshIdMissionData = (): void => {
        const userId = this.common.session.userId ?? this.unauthorizedUserId;

        if (userId === null) {
            return;
        }

        this.identityVerifyProgress.get([userId]).refresh();
    };
}
